import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Tiles } from 'wsm-tiles';
import {
	usePrefs,
	useLabels,
	useRequestData,
	useSitemap
} from 'wsm-common-data';

import {
	inventoryCountsSelector,
	loadingInventoryCountsSelector
} from '../../server/selectors';
import { getModelLink } from '../../utils/getModelLink';
import { buildModelTileData } from '../../utils/buildModelTileData';

import MiniGridPager from '../MiniGridPager';

// Always 6 tiles per page: 3 rows of 2 columns, or 2 rows of 3 columns (the
// column count is chosen by CSS from the container width, see main.scss).
const PAGE_SIZE = 6;

/**
 * Mobile counterpart of the `tilesCarousel` display style: the same Model
 * tiles, same in-stock-only vehicles (filtered upstream in
 * useProcessVehicles) and same tile data/links as the desktop carousel, laid
 * out as a paged mini-grid (6 tiles per page: 3 rows of 2 or 2 rows of 3) with a centered
 * prev/next pair instead of a swipe strip.
 */
const MobileMiniGrid = ({ vehicles }) => {
	const prefs = usePrefs();
	const labels = useLabels();
	const sitemap = useSitemap();
	const { locale } = useRequestData();
	const inventoryCounts = useSelector(inventoryCountsSelector);
	const loadingInventoryCounts = useSelector(loadingInventoryCountsSelector);
	const [page, setPage] = useState(0);
	const containerRef = useRef(null);
	const scrollOnPageChange = useRef(false);

	const { modelLinkAlias, modelLinkParameters, noInventoryFallbackUrl } =
		prefs;

	const modelLinkPath = `${sitemap.get(modelLinkAlias)}`;
	const hasInventoryData =
		!loadingInventoryCounts.error &&
		Object.keys(inventoryCounts).length > 0;

	const pageCount = Math.max(1, Math.ceil(vehicles.length / PAGE_SIZE));
	// Clamped while rendering so the page can never point past the end.
	const currentPage = Math.min(page, pageCount - 1);

	// Go back to the first page when the number of models (a different tab)
	// changes. Keyed on the length, not the array:
	// some callers build a new array with the same models on every render, and
	// that must not throw the shopper back to page 1 mid-browse.
	useEffect(() => {
		setPage(0);
	}, [vehicles.length]);

	// After the shopper pages, bring the top of the grid back into view so the
	// first row of the new page is not left off-screen. Only user paging
	// scrolls: the initial render and the resets above do not.
	useEffect(() => {
		if (!scrollOnPageChange.current) return;
		scrollOnPageChange.current = false;
		if (containerRef.current?.scrollIntoView) {
			const reduceMotion = window.matchMedia?.(
				'(prefers-reduced-motion: reduce)'
			)?.matches;
			containerRef.current.scrollIntoView({
				behavior: reduceMotion ? 'auto' : 'smooth',
				block: 'nearest'
			});
		}
	}, [currentPage]);

	const handlePageChange = (nextPage) => {
		scrollOnPageChange.current = true;
		setPage(nextPage);
	};

	// Spoken page position, e.g. "Page 2 of 4" ({0} = page, {1} = total).
	const pageStatus = labels
		.get('PAGE_X_OF_Y')
		.replace('{0}', currentPage + 1)
		.replace('{1}', pageCount);

	const tileData = vehicles
		.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)
		.map((vehicle) =>
			buildModelTileData({
				vehicle,
				modelLink: getModelLink(
					modelLinkPath,
					modelLinkParameters,
					vehicle,
					noInventoryFallbackUrl,
					inventoryCounts
				),
				labels,
				locale,
				inventoryCounts,
				hasInventoryData,
				prefs
			})
		);

	// Always the medium tile: the Figma mobile spec is "Medium-size Model
	// Tiles", and the large tile (fixed 240px) cannot fit two columns on a
	// 375px phone, so the `tileSize` pref is intentionally not applied here.
	return (
		<div
			ref={containerRef}
			className="mobile-mini-grid px-4"
			data-testid="mobile-mini-grid"
		>
			<Tiles
				type="model"
				data={tileData}
				layout="grid"
				tileSize="md"
				className="mx-auto"
			/>
			{pageCount > 1 && (
				<span className="sr-only" role="status">
					{pageStatus}
				</span>
			)}
			<MiniGridPager
				page={currentPage}
				pageCount={pageCount}
				onPageChange={handlePageChange}
			/>
		</div>
	);
};

MobileMiniGrid.propTypes = {
	vehicles: PropTypes.arrayOf(
		PropTypes.shape({
			year: PropTypes.number,
			make: PropTypes.string,
			model: PropTypes.string,
			modelInv: PropTypes.string,
			gvBodyStyle: PropTypes.string,
			image: PropTypes.string,
			inventoryCount: PropTypes.number
		})
	).isRequired
};

export default MobileMiniGrid;
